import { preview } from '@/components/form/preview'
import { Tooltip } from '@/components/primitives/Tooltip'
import { MoreHoriz } from '@mui/icons-material'
import { Box, IconButton, Menu, MenuItem, Stack } from '@mui/material'
import { useCallback, useState } from 'react'
import { useCopyReasonsForNotCompletedOrdersModal } from '../../../modals/CopyReasonsForNotCompletedOrdersModal'
import { useTexts } from '../hooks/useTexts'

interface Props {
  reasonsForNotCompletedOrders: string[]
  territories: Record<string, uui.domain.client.rm.Territory>
}

export function ReasonsForNotCompletedOrders(props: Props) {
  const { reasonsForNotCompletedOrders, territories } = props
  const texts = useTexts()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const noReasons = reasonsForNotCompletedOrders.length === 0
  const singleTerritory = Object.keys(territories).length === 1

  const cannotCopyReasons = noReasons || singleTerritory
  const tooltipTitle = noReasons ? texts.noReasons : singleTerritory ? texts.oneTerritory : ''

  const {
    show: showCopyReasonsForNotCompletedOrdersModal,
    Modal: CopyReasonsForNotCompletedOrdersModal,
  } = useCopyReasonsForNotCompletedOrdersModal()

  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleCopyClick = useCallback(() => {
    showCopyReasonsForNotCompletedOrdersModal()
    handleMenuClose()
  }, [showCopyReasonsForNotCompletedOrdersModal, handleMenuClose])

  return (
    <>
      <Stack direction="column" data-testid="driver-activity-reasons-not-completed-orders">
        <Stack direction="row" alignItems="center">
          <preview.Label>{`${texts.reasonsTitle} (${reasonsForNotCompletedOrders.length || 0})`}</preview.Label>
          <Box marginLeft="auto" display="flex" alignItems="flex-start">
            <Tooltip title={texts.more} placement="top">
              <IconButton
                onClick={handleMenuClick}
                disabled={false}
                data-testid="driver-activity-reasons-not-completed-orders-menu-btn"
              >
                <MoreHoriz />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>

        {!noReasons && (
          <preview.Field label={null}>
            {reasonsForNotCompletedOrders.map((reason, index) => (
              <preview.Value variant="alternative" key={index}>
                {reason}
              </preview.Value>
            ))}
          </preview.Field>
        )}

        <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
          <Tooltip title={tooltipTitle} placement="left" disabled={!cannotCopyReasons}>
            <MenuItem
              onClick={handleCopyClick}
              disabled={cannotCopyReasons}
              data-testid="driver-activity-reasons-not-completed-orders-copy-btn"
              data-trackid="driver-activity-reasons-not-completed-orders-copy-btn"
            >
              {texts.copySettingsToTerritory}
            </MenuItem>
          </Tooltip>
        </Menu>
      </Stack>

      <CopyReasonsForNotCompletedOrdersModal />
    </>
  )
}
